// src/services/egliseService.ts
import api from './api';
import { Eglise, EgliseUsersResponse, EgliseEventsResponse, AvailableAdmin, CreateChurchRequest, ChurchResponse, UpdateEgliseRequest } from '../types/church';

const EGLISE_ENDPOINTS = {
  list: '/eglises/',
  details: (id: number) => `/eglises/${id}`,
  users: (id: number) => `/eglises/${id}/users`,
  userAction: (egliseId: number, userId: number) => `/eglises/${egliseId}/users/${userId}`,
  events: (id: number) => `/eglises/${id}/events`,
  join: (id: number) => `/eglises/${id}/join`,
  availableAdmins: '/eglises/available-admins',
  createWithAdmin: '/eglises/create-with-admin',
  myChurch: '/eglises/my-church',
};

export const getAllEglises = async (): Promise<Eglise[]> => {
  console.log('🏛️ [EgliseService] Fetching all églises...');
  const response = await api.get<Eglise[]>(EGLISE_ENDPOINTS.list);
  console.log('✅ [EgliseService] Églises fetched:', response.data);
  return response.data;
};

export const getEgliseById = async (id: number): Promise<Eglise> => {
  console.log('🏛️ [EgliseService] Fetching église by id:', id);
  const response = await api.get<Eglise>(EGLISE_ENDPOINTS.details(id));
  console.log('✅ [EgliseService] Église details fetched:', response.data);
  return response.data;
};

export const getEgliseUsers = async (id: number): Promise<EgliseUsersResponse> => {
  console.log('👥 [EgliseService] Fetching users for église:', id);
  const response = await api.get<EgliseUsersResponse>(EGLISE_ENDPOINTS.users(id));
  console.log('✅ [EgliseService] Users fetched:', response.data);
  return response.data;
};

export const getEgliseEvents = async (id: number): Promise<EgliseEventsResponse> => {
  console.log('📅 [EgliseService] Fetching events for église:', id);
  const response = await api.get<EgliseEventsResponse>(EGLISE_ENDPOINTS.events(id));
  console.log('✅ [EgliseService] Events fetched:', response.data);
  return response.data;
};

// Super Admin: Récupérer les utilisateurs disponibles pour être admin
export const getAvailableAdmins = async (skip: number = 0, limit: number = 100): Promise<AvailableAdmin[]> => {
  console.log('👤 [EgliseService] Fetching available admins...');
  const response = await api.get<AvailableAdmin[]>(EGLISE_ENDPOINTS.availableAdmins, {
    params: { skip, limit }
  });
  console.log('✅ [EgliseService] Available admins fetched:', response.data);
  return response.data;
};

// Super Admin: Créer une église avec un admin assigné
export const createChurchWithAdmin = async (data: CreateChurchRequest): Promise<ChurchResponse> => {
  console.log('🏛️ [EgliseService] Creating church with admin:', data);
  const response = await api.post<ChurchResponse>(EGLISE_ENDPOINTS.createWithAdmin, data);
  console.log('✅ [EgliseService] Church created:', response.data);
  return response.data;
};

// Admin: Récupérer son église
export const getMyChurch = async (): Promise<ChurchResponse> => {
  console.log('🏛️ [EgliseService] Fetching my church...');
  const response = await api.get<ChurchResponse>(EGLISE_ENDPOINTS.myChurch);
  console.log('✅ [EgliseService] My church fetched:', response.data);
  return response.data;
};

// Admin: Modifier son église
export const updateEglise = async (id: number, data: UpdateEgliseRequest): Promise<Eglise> => {
  console.log('🏛️ [EgliseService] Updating église:', id, data);
  const response = await api.put<Eglise>(EGLISE_ENDPOINTS.details(id), data);
  console.log('✅ [EgliseService] Église updated:', response.data);
  return response.data;
};

// Admin: Ajouter un membre à l'église
export const addUserToEglise = async (egliseId: number, userId: number): Promise<void> => {
  console.log('👤 [EgliseService] Adding user to église:', { egliseId, userId });
  await api.post(EGLISE_ENDPOINTS.userAction(egliseId, userId));
  console.log('✅ [EgliseService] User added to église');
};

// Admin: Retirer un membre de l'église
export const removeUserFromEglise = async (egliseId: number, userId: number): Promise<void> => {
  console.log('👤 [EgliseService] Removing user from église:', { egliseId, userId });
  await api.delete(EGLISE_ENDPOINTS.userAction(egliseId, userId));
  console.log('✅ [EgliseService] User removed from église');
};

// User: Rejoindre une église
export const joinEglise = async (egliseId: number): Promise<void> => {
  console.log('🏛️ [EgliseService] Joining église:', egliseId);
  await api.post(EGLISE_ENDPOINTS.join(egliseId));
  console.log('✅ [EgliseService] Joined église successfully');
};

// Super Admin: Activer/Désactiver une église
export const toggleEgliseStatus = async (egliseId: number): Promise<Eglise> => {
  console.log('🏛️ [EgliseService] Toggling église status:', egliseId);
  const response = await api.post<Eglise>(`/eglises/${egliseId}/toggle-status`);
  console.log('✅ [EgliseService] Église status toggled:', response.data);
  return response.data;
};

// Super Admin: Supprimer une église
export const deleteEglise = async (egliseId: number): Promise<void> => {
  console.log('🏛️ [EgliseService] Deleting église:', egliseId);
  await api.delete(`/eglises/${egliseId}`);
  console.log('✅ [EgliseService] Église deleted');
};

// ==================== IMAGE UPLOAD ====================

export interface ImageUploadResponse {
  success: boolean;
  message: string;
  url: string;
  thumbnail_url?: string;
  file_size?: number;
}

// Admin: Upload logo de l'église
export const uploadEgliseLogo = async (
  egliseId: number,
  file: File,
  onProgress?: (progress: number) => void
): Promise<ImageUploadResponse> => {
  console.log('🖼️ [EgliseService] Uploading église logo:', egliseId);
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post<ImageUploadResponse>(
    `/eglises/${egliseId}/logo`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000, // 60 seconds
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    }
  );
  console.log('✅ [EgliseService] Logo uploaded:', response.data);
  return response.data;
};

// Admin: Supprimer logo de l'église
export const deleteEgliseLogo = async (egliseId: number): Promise<void> => {
  console.log('🗑️ [EgliseService] Deleting église logo:', egliseId);
  await api.delete(`/eglises/${egliseId}/logo`);
  console.log('✅ [EgliseService] Logo deleted');
};

// Admin: Upload cover image de l'église
export const uploadEgliseCover = async (
  egliseId: number,
  file: File,
  onProgress?: (progress: number) => void
): Promise<ImageUploadResponse> => {
  console.log('🖼️ [EgliseService] Uploading église cover:', egliseId);
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post<ImageUploadResponse>(
    `/eglises/${egliseId}/cover`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000, // 60 seconds
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    }
  );
  console.log('✅ [EgliseService] Cover uploaded:', response.data);
  return response.data;
};

// Admin: Supprimer cover image de l'église
export const deleteEgliseCover = async (egliseId: number): Promise<void> => {
  console.log('🗑️ [EgliseService] Deleting église cover:', egliseId);
  await api.delete(`/eglises/${egliseId}/cover`);
  console.log('✅ [EgliseService] Cover deleted');
};
